import { Tooltip } from "@/base";
import { formatDate, formatTimeAgo } from "@sr-partner-portal/common";
import { memo, useMemo } from "react";

interface DateLabelProps {
    dateValue?: string | Date;
    tooltipLabel?: string;
    withTooltip?: boolean;
    withTimeAgo?: boolean;
    withTimeAgoLabel?: boolean;
    className?: string;
}

const DateLabel = memo<DateLabelProps>(
    ({
        dateValue,
        tooltipLabel,
        withTooltip,
        withTimeAgo,
        withTimeAgoLabel,
        className,
    }) => {
        const formattedDate = useMemo(() => {
            if (!dateValue) {
                return "-";
            }

            return formatDate(dateValue, "dd. LLL.");
        }, [dateValue]);

        const fullFormattedDate = useMemo(() => {
            if (!dateValue) {
                return "-";
            }

            return formatDate(dateValue, "EEE dd. LLL. yyyy");
        }, [dateValue]);

        const formattedTimeAgo = useMemo(() => {
            if (!dateValue) {
                return null;
            }

            return formatTimeAgo(dateValue);
        }, [dateValue]);

        const dateLabel = withTimeAgoLabel
            ? `${formattedDate}, ${formattedTimeAgo}`
            : formattedDate;

        if (!withTooltip) {
            return dateLabel;
        }

        return (
            <Tooltip
                classNames={{
                    content: "truncate break-all whitespace-pre-line px-5 py-4",
                }}
                delay={200}
                content={
                    withTimeAgo ? (
                        <div className="flex flex-col items-center gap-1">
                            {tooltipLabel
                                ? `${tooltipLabel} ${fullFormattedDate}`
                                : fullFormattedDate}
                            <div className="text-foreground-500 text-tiny">
                                {formattedTimeAgo}
                            </div>
                        </div>
                    ) : (
                        fullFormattedDate
                    )
                }
            >
                <span className={className}>{dateLabel}</span>
            </Tooltip>
        );
    },
);

export { DateLabel };
